import React, {useEffect, useState} from 'react';
import useApi from "../../../utils/useApi";
import {Link, useNavigate, useParams, useSearchParams} from "react-router-dom";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";

import {ArrowRight, Eye, Folder, LineChart, Mail, Plus} from "lucide-react";
import DocumentsNeededList from "../../DocumentsNeededList/DocumentsNeededList";
import ClientAccountActions from "../ClientAccountActions/ClientAccountActions";

const ClientAccount = ({handleShowHeader, handleShowSettingsHeader}) => {
    const { getClient } = useApi();
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [showArchived, setShowArchived] = useState(false);
    const [client, setClient] = useState(null);
    const [activeAccounts, setActiveAccounts] = useState(null);
    const [archivedAccounts, setArchivedAccounts] = useState(null);
    const [currentDocument, setCurrentDocument] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    // Year States
    // Get current year for the year selector range
    const currentYear = new Date().getFullYear();
    // Default to previous tax year (current year - 1)
    const defaultYear = (currentYear - 1).toString();
    const [selectedYear, setSelectedYear] = useState(searchParams.get('year') ?? defaultYear);

    // Generate year options from 2022 to current year
    const yearOptions = Array.from(
        { length: currentYear - 2021 },
        (_, i) => (2022 + i).toString()
    );

    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);
        initialize();
    }, []);

    const initialize = async () => {
        try {
            setIsLoading(true);
            const response = await getClient(id);
            console.log(response);
            setClient(response.client);
            setActiveAccounts(response.users_active);
            setArchivedAccounts(response.users_archived)
        }
        catch (error) {
            console.error('Failed to fetch client:', error);
        }
        finally {
            setIsLoading(false);
        }
    }

    const handleDocumentClick = (documentId) => {
        console.log(client);
        // Find the document in documentsNeeded array
        const selectedDocument = client.documentsNeeded.find(doc => doc.id === documentId);
        if (selectedDocument) {
            // Set the current document in parent state
            setCurrentDocument(selectedDocument);
            // Navigate to upload-documents page
            navigate(`/client/${client.id}/upload`);
        }
    };


    const DocumentProgress = ({ user }) => (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex justify-between items-center">
                <span className="text-gray-700 dark:text-gray-200">
                  Documents Progress
                </span>
                <span className="text-gray-700 dark:text-gray-200">
          {user.finishedCount} / {user.documentsCount}
        </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mt-2">
                <div
                    className="bg-blue-600 h-2.5 rounded-full"
                    style={{ width: `${(user.finishedCount / user.documentsCount) * 100}%` }}
                />
            </div>
            <DocumentsNeededList documents={user.documentsNeeded} onDocumentClick={handleDocumentClick} />
        </div>
    );

    const onAddAccount = () => {

    }

    if (isLoading) {
        return <LoadingScreen/>;
    }

    if (!client) {
        return <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
            <p className="text-gray-500 dark:text-gray-400">Client not found</p>
        </div>;
    }

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 page-container relative z-10">
            {/* Header */}
            <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                                Client Account Details
                            </h1>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                <Link to={`/client/${client.id}/edit`}>{client.profile.client}</Link>
                            </p>
                        </div>
                        <button
                            onClick={onAddAccount}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Add Account <Plus className="w-4 h-4"/>
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Active Accounts */}
                {activeAccounts.length > 0 ? (
                    <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                            Active Accounts
                        </h2>
                        <div className="space-y-6">
                            {activeAccounts.map(user => (
                                <div key={user.id} className="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0">
                                    <ClientAccountActions user={user}
                                                          isArchived={false}
                                                          includeProgressBtn={true}
                                                          onArchiveSuccess={initialize}
                                    year={selectedYear}
                                    />
                                    <DocumentProgress user={user}/>
                                </div>
                            ))}
                        </div>
                    </section>
                ) : (
                     <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
                         <p className="text-gray-500 dark:text-gray-400">No active accounts</p>
                     </div>
                 )}

                {/* Archived Accounts */}
                {archivedAccounts.length > 0 && (
                    <>
                        <button
                            onClick={() => setShowArchived(!showArchived)}
                            className="w-full mt-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors mb-6"
                        >
                            {showArchived ? 'Hide' : 'Show'} Archived Accounts ({archivedAccounts.length ?? 0})
                        </button>

                        {showArchived && (
                            <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                                    Archived Accounts
                                </h2>
                                <div className="space-y-6">
                                    {archivedAccounts.map(user => (
                                        <div key={user.id} className="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0">
                                            <ClientAccountActions user={user}
                                                                  isArchived={true}
                                                                  includeProgressBtn={true}
                                                                  onArchiveSuccess={initialize}/>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </main>
        </div>
    );
};

export default ClientAccount;
